// frontend/src/components/games/VoiceChat.jsx
// Voice Chat Component for Friday Fun Zone

import React, { useState, useEffect, useRef } from 'react';

const VoiceChat = ({ roomId }) => {
  const [isMuted, setIsMuted] = useState(false);
  const [isDeafened, setIsDeafened] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [isMinimized, setIsMinimized] = useState(false);
  const localStreamRef = useRef(null);
  const audioContextRef = useRef(null);

  useEffect(() => {
    initializeVoiceChat();
    
    return () => {
      cleanup();
    };
  }, [roomId]);

  const initializeVoiceChat = async () => {
    try {
      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      localStreamRef.current = stream;
      
      // Initialize audio context for visualization
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      
      console.log('Voice chat initialized for room:', roomId);
    } catch (error) {
      console.error('Failed to initialize voice chat:', error);
      alert('Could not access microphone. Please check permissions.');
    }
  };

  const cleanup = () => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
  };

  const toggleMute = () => {
    if (localStreamRef.current) {
      const audioTrack = localStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = isMuted;
        setIsMuted(!isMuted);
      }
    }
  };

  const toggleDeafen = () => {
    setIsDeafened(!isDeafened);
    // In real implementation, this would mute all incoming audio
  };

  const getVolumeIndicator = (speaking) => {
    return speaking ? '🔊' : '🔇';
  };

  if (isMinimized) {
    return (
      <div className="fixed bottom-4 right-4 z-50">
        <button
          onClick={() => setIsMinimized(false)}
          className="bg-purple-600 text-white p-4 rounded-full shadow-2xl hover:bg-purple-700 transition"
        >
          🎙️ {participants.length}
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 bg-gray-800 rounded-2xl shadow-2xl p-6 w-80 z-50 border-2 border-purple-500">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-white font-bold text-lg flex items-center gap-2">
          🎙️ Voice Chat
          <span className="text-sm text-gray-400">#{roomId}</span>
        </h3>
        <button
          onClick={() => setIsMinimized(true)}
          className="text-gray-400 hover:text-white"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Participants */}
      <div className="mb-4 max-h-48 overflow-y-auto">
        <div className="space-y-2">
          <div className="bg-gray-700 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-sm">You</span>
              </div>
              <span className="text-white font-semibold">You</span>
            </div>
            <span className="text-xl">
              {isMuted ? '🔇' : '🔊'}
            </span>
          </div>
          
          {participants.map((participant, idx) => (
            <div key={idx} className="bg-gray-700 rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-sm">
                    {participant.name.charAt(0)}
                  </span>
                </div>
                <span className="text-white">{participant.name}</span>
              </div>
              <span className="text-xl">
                {getVolumeIndicator(participant.speaking)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className="flex gap-2">
        <button
          onClick={toggleMute}
          className={`flex-1 py-3 px-4 rounded-lg font-bold transition flex items-center justify-center gap-2 ${
            isMuted 
              ? 'bg-red-600 hover:bg-red-700 text-white' 
              : 'bg-gray-700 hover:bg-gray-600 text-white'
          }`}
        >
          {isMuted ? '🔇 Unmute' : '🎤 Mute'}
        </button>
        
        <button
          onClick={toggleDeafen}
          className={`flex-1 py-3 px-4 rounded-lg font-bold transition flex items-center justify-center gap-2 ${
            isDeafened 
              ? 'bg-red-600 hover:bg-red-700 text-white' 
              : 'bg-gray-700 hover:bg-gray-600 text-white'
          }`}
        >
          {isDeafened ? '🔇 Undeafen' : '🔊 Deafen'}
        </button>
      </div>

      {/* Info */}
      <div className="mt-4 text-center text-xs text-gray-400">
        <p>Voice chat is active</p>
        <p className="mt-1">Make sure your microphone is enabled</p>
      </div>
    </div>
  );
};

export default VoiceChat;
